import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function Navbar() {
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Services', path: '/services' },
    { label: 'Donate', path: '/donate' },
    { label: 'Contact', path: '/contact' },
  ];

  const handleLogout = async () => {
    await signOut();
    navigate('/');
    setUserMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="w-full px-6 md:px-10 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ${scrolled ? 'bg-brand-500' : 'bg-white/20 backdrop-blur-sm'}`}>
            <img
              src="https://readdy.ai/api/search-image?query=A%20minimalist%20professional%20NGO%20logo%20mark%20featuring%20abstract%20interlocking%20human%20figures%20forming%20a%20circular%20bond%20symbol%2C%20warm%20coral%20orange%20and%20deep%20forest%20green%20colors%20on%20a%20clean%20pure%20white%20background%2C%20representing%20unity%20compassion%20and%20humanitarian%20bonds%20between%20people%2C%20modern%20flat%20vector%20style%20design%20with%20smooth%20organic%20curves%2C%20no%20text%20no%20letters%2C%20geometric%20shapes%20symbolizing%20community%20connection%2C%20centered%20composition%2C%20simple%20clean%20minimalist%20style&width=200&height=200&seq=kibi-logo-001&orientation=squarish"
              alt="KIBI Logo"
              className="w-full h-full object-cover"
            />
          </div>
          <span className={`hidden md:block text-sm font-bold tracking-tight leading-tight ${scrolled ? 'text-gray-900' : 'text-white'}`}>
            KELAB<br />IKATAN BINA INSAN
          </span>
          <span className={`md:hidden text-sm font-bold tracking-tight ${scrolled ? 'text-gray-900' : 'text-white'}`}>
            KIBI
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium transition-colors duration-300 hover:opacity-70 ${
                scrolled ? 'text-gray-700' : 'text-white/90'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-4">
          {user ? (
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full transition-all ${
                  scrolled
                    ? 'text-gray-700 hover:bg-gray-100'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${scrolled ? 'bg-brand-500 text-white' : 'bg-white/20 text-white'}`}>
                  {profile?.full_name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
                </div>
                <span className="max-w-[120px] truncate">{profile?.full_name || user.email.split('@')[0]}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {userMenuOpen && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                  <Link
                    to="/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <i className="ri-user-line"></i>
                    My Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left"
                  >
                    <i className="ri-logout-box-r-line"></i>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className={`text-sm font-medium px-5 py-2.5 rounded-full transition-all duration-300 ${
                scrolled
                  ? 'text-gray-700 hover:bg-gray-100'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Log In
            </Link>
          )}
          <Link
            to="/donate"
            className="text-sm font-semibold px-6 py-2.5 rounded-full bg-brand-500 text-white hover:bg-brand-600 transition-all duration-300 shadow-lg shadow-brand-500/25"
          >
            Donate Now
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
            scrolled ? 'text-gray-900' : 'text-white'
          }`}
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-100">
          <div className="flex flex-col py-4 px-6 gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className="text-gray-700 font-medium py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-gray-100">
              {user ? (
                <>
                  <Link
                    to="/profile"
                    onClick={() => setMobileOpen(false)}
                    className="text-gray-700 font-medium py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    My Profile
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileOpen(false);
                    }}
                    className="text-left text-red-600 font-medium py-3 px-4 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="text-center text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Log In
                </Link>
              )}
              <Link
                to="/donate"
                onClick={() => setMobileOpen(false)}
                className="text-center font-semibold py-3 rounded-full bg-brand-500 text-white"
              >
                Donate Now
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}